import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, RefreshCcw, ShieldCheck } from "lucide-react";
import Header from "@/components/site/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_KEY = "impactvest_admin_token";

export default function AdminAdoption() {
  const [token, setToken] = useState(() => sessionStorage.getItem(ADMIN_KEY) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Adoption · Platform Admin"; }, []);

  const load = async (tok = token) => {
    if (!tok) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/adoption/summary`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setData(res.data);
      sessionStorage.setItem(ADMIN_KEY, tok);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to load adoption metrics.");
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(token); }, []); // eslint-disable-line

  const exportCsv = async () => {
    const res = await axios.get(`${API}/admin/adoption/export`, {
      params: { format: "csv" }, headers: { Authorization: `Bearer ${token}` }, responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = url; a.download = `adoption-${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  if (!data) {
    return (
      <div><Header />
        <main className="pt-28 pb-24 iv-container max-w-md">
          <div className="iv-eyebrow mb-4">Platform Admin</div>
          <h1 className="font-serif-display text-[36px] text-iv-fg">Adoption</h1>
          <label className="iv-field mt-6">
            <span>Admin bearer token</span>
            <input type="password" value={token} onChange={(e) => setToken(e.target.value)}
              data-testid="admin-adoption-token" placeholder="impactvest-admin-pilot" />
          </label>
          <button onClick={() => load()} disabled={loading} className="iv-btn iv-btn--primary mt-6" data-testid="admin-adoption-submit">
            <ShieldCheck size={14} /> {loading ? "Loading…" : "Open dashboard"}
          </button>
        </main>
      </div>
    );
  }

  const t = data.totals;

  return (
    <div><Header />
      <main className="pt-28 pb-24 iv-container" data-testid="admin-adoption-dashboard">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="iv-eyebrow mb-3">Platform Admin</div>
            <h1 className="font-serif-display text-[38px] md:text-[48px] text-iv-fg leading-tight">Adoption</h1>
            <p className="text-[13.5px] text-iv-fg-3 mt-2">Generated {data.generated_at?.slice(0,19).replace("T"," ")} UTC</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load()} className="iv-btn"><RefreshCcw size={14} /> Refresh</button>
            <button onClick={exportCsv} className="iv-btn iv-btn--primary"><Download size={14} /> Export CSV</button>
          </div>
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Metric label="Registered users" v={t.registered_users} />
          <Metric label="Verified users" v={t.verified_users} />
          <Metric label="Activated test users" v={t.activated_test_users} />
          <Metric label="Workflow completers" v={t.workflow_completers} />
          <Metric label="Engaged · 7d" v={t.engaged_users_7d} />
          <Metric label="Engaged · 30d" v={t.engaged_users_30d} />
          <Metric label="Qualified family-office users" v={t.qualified_family_office_users} />
          <Metric label="Qualified organizations" v={t.qualified_organizations} />
          <Metric label="Distinct organizations" v={t.distinct_organizations} />
          <Metric label="Live applications submitted" v={t.live_applications_submitted} />
          <Metric label="Approved live orgs" v={t.approved_live_organizations} />
          <Metric label="Activation rate" v={`${data.activation_rate}%`} />
        </div>

        {/* Rates */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Metric label="Core-workflow completion" v={`${data.core_workflow_completion_rate}%`} />
          <Metric label="Source-citation engagement" v={`${data.source_citation_engagement_rate}%`} />
          <Metric label="7-day return rate" v={`${data.seven_day_return_rate}%`} />
          <Metric label="30-day return rate" v={`${data.thirty_day_return_rate}%`} />
        </div>

        {/* Funnel */}
        <div className="iv-panel p-6 mb-10">
          <h2 className="font-serif-display text-[26px] text-iv-fg mb-4">Funnel</h2>
          <table className="w-full text-[13px]" data-testid="admin-adoption-funnel">
            <thead>
              <tr className="text-iv-fg-3 font-mono text-[10.5px] tracking-[0.14em] uppercase text-left">
                <th className="pb-3">Stage</th><th className="pb-3">Users</th><th className="pb-3">Drop-off</th><th className="pb-3">Drop-off rate</th>
              </tr>
            </thead>
            <tbody>
              {(data.funnel_abandonment?.stages || []).map((s) => (
                <tr key={s.stage} className="border-t border-iv-line">
                  <td className="py-2 text-iv-fg">{s.stage.replace(/_/g, " ")}</td>
                  <td className="py-2 font-mono">{s.count}</td>
                  <td className="py-2 font-mono">{s.dropoff_from_previous ?? "—"}</td>
                  <td className="py-2 font-mono">{s.dropoff_rate_pct != null ? `${s.dropoff_rate_pct}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* By org type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <BreakdownTable title="Registered by organization type" data={data.by_organization_type} />
          <BreakdownTable title="Workflow completers by org type" data={data.conversion_by_organization_type} />
          <BreakdownTable title="Registrations by referral source" data={data.conversion_by_referral_source} />
          <div className="iv-panel p-6">
            <div className="iv-eyebrow mb-3">Median time</div>
            <TimePair label="Time to first meaningful action" seconds={data.median_time_to_first_action_seconds} />
            <TimePair label="Time to workflow completion" seconds={data.median_time_to_workflow_completion_seconds} />
          </div>
        </div>
      </main>
    </div>
  );
}

function Metric({ label, v }) {
  return (
    <div className="iv-panel p-4">
      <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-iv-fg-3">{label}</div>
      <div className="font-serif-display text-[28px] text-iv-fg mt-1">{v ?? "—"}</div>
    </div>
  );
}
function BreakdownTable({ title, data }) {
  const entries = Object.entries(data || {});
  return (
    <div className="iv-panel p-6">
      <div className="iv-eyebrow mb-3">{title}</div>
      {entries.length === 0 ? (
        <p className="text-[13px] text-iv-fg-3">No data yet.</p>
      ) : (
        <ul className="divide-y divide-iv-line">
          {entries.sort((a,b) => b[1] - a[1]).map(([k, v]) => (
            <li key={k} className="flex items-center justify-between py-2 text-[13.5px]">
              <span className="text-iv-fg">{k}</span>
              <span className="font-mono text-iv-fg-2">{v}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
function TimePair({ label, seconds }) {
  const val = seconds == null ? "—" :
              seconds < 90 ? `${Math.round(seconds)}s` :
              seconds < 3600 ? `${Math.round(seconds/60)}m` :
              `${(seconds/3600).toFixed(1)}h`;
  return (
    <div className="flex items-center justify-between py-2 border-t border-iv-line text-[13.5px] first:border-t-0">
      <span className="text-iv-fg-2">{label}</span>
      <span className="font-mono text-iv-fg">{val}</span>
    </div>
  );
}
